import { HttpService } from '@/shared/api/services/HttpService';
import { buildUrlForServiceName } from '@/connection/url-retriever';
import conf from '@/shared/config/useConfig';
import type { BeziehungNutzerOrganisation, Nutzer, Recht, Rolle } from '@/types/Nutzer.ts';
import type {
  TabellenKonfiguration,
  TabellenKonfigurationsKeys,
} from '@/common/NutzerKonfiguration/types.ts';
import type { FilterOptionen } from '@/components/datatable/types/FilterObject.ts';
import type {
  NutzerFilter,
  NutzerFilterResponse,
} from '@/connection/filter-helper/filter-helper-nutzer.ts';

export class SharedNutzerverwaltung extends HttpService {
  protected baseUrl = `${buildUrlForServiceName(
    conf.partnerserviceName,
  )}/partnerservice/api/private/v2/nutzerverwaltung`;

  async updateNutzer(nutzer: Nutzer): Promise<void> {
    return this.httpClient.put(`${this.baseUrl}/benutzer`, { data: nutzer });
  }

  async nutzerWiederherstellen(nutzer: Nutzer): Promise<void> {
    return this.httpClient.post(`${this.baseUrl}/benutzer/wiederherstellen`, {
      data: nutzer,
    });
  }

  async erstelleNutzer(nutzer: Nutzer): Promise<void> {
    return this.httpClient.post(`${this.baseUrl}/benutzer`, {
      data: nutzer,
    });
  }

  async holeEinzelnenBenutzer(id: string): Promise<Nutzer> {
    return this.httpClient.get(`${this.baseUrl}/benutzer/${id}`);
  }

  async holeAlleRollen(): Promise<Rolle[]> {
    return this.httpClient.get(`${this.baseUrl}/rollen/alle`);
  }

  async holeEigeneRechte(): Promise<Recht[]> {
    return this.httpClient.get(`${this.baseUrl}/rechte/nutzer`);
  }

  async holeAlleRechte(): Promise<Recht[]> {
    return this.httpClient.get(`${this.baseUrl}/rechte`);
  }

  async erstelleRolle(rolle: Rolle): Promise<void> {
    return this.httpClient.post(`${this.baseUrl}/rollen/erstellen`, { data: rolle });
  }

  async holeEinzelneRolle(rolleName: string): Promise<Rolle> {
    return this.httpClient.get(`${this.baseUrl}/rollen`, { params: { name: rolleName } });
  }

  async loescheRolle(rolleName: string): Promise<void> {
    return this.httpClient.delete(`${this.baseUrl}/rollen`, { params: { name: rolleName } });
  }

  async aktualisiereRolle(rolle: Rolle, rolleName: string): Promise<void> {
    return this.httpClient.post(`${this.baseUrl}/rollen/aktualisieren`, {
      data: rolle,
      params: { name: rolleName },
    });
  }

  async erstelleBenutzer(nutzer: Nutzer): Promise<void> {
    return this.httpClient.post(`${this.baseUrl}/benutzer`, {
      data: nutzer,
    });
  }

  async loescheNutzer(nutzerId: string): Promise<Nutzer> {
    return this.httpClient.post(`${this.baseUrl}/benutzer/${nutzerId}/loeschung`);
  }

  async benutzerWiederherstellen(nutzer: Nutzer): Promise<void> {
    return this.httpClient.post(`${this.baseUrl}/benutzer/wiederherstellen`, { data: nutzer });
  }

  async deaktiviereEigenenNutzer(token: string): Promise<void> {
    return this.httpClient.post(`${this.baseUrl}/benutzer/deaktivierung`, { data: token });
  }

  async pruefeBeziehungNutzerOrganisation(email: string): Promise<BeziehungNutzerOrganisation> {
    return this.httpClient.get(`${this.baseUrl}/benutzer/beziehungeigeneorganisation/${email}`);
  }

  async einstellungErhaltNutzerEMails(erhaltNutzerEMails: boolean): Promise<void> {
    return this.httpClient.post(`${this.baseUrl}/email/einstellung`, { data: erhaltNutzerEMails });
  }

  async holeNutzerAnhandEmail(email: string): Promise<Nutzer> {
    return this.httpClient.get(`${this.baseUrl}/benutzer/email/${email}`);
  }

  async holeAlleBenutzer(): Promise<Nutzer[]> {
    return this.httpClient.get(`${this.baseUrl}/benutzer`);
  }

  async speicherNutzerTabellenKonfiguration(
    key: TabellenKonfigurationsKeys,
    konfiguration: TabellenKonfiguration,
  ): Promise<void> {
    return this.httpClient.post(`${this.baseUrl}/${key}/einstellung`, { data: konfiguration });
  }

  /** Wenn nichts gefunden wird zu dem Key wird eine leere Response zurückgegeben,
   daraus macht Axios einen leeren String, daher der union return type.
   */
  async holeNutzerTabellenKonfiguration(
    key: TabellenKonfigurationsKeys,
  ): Promise<TabellenKonfiguration | string> {
    return await this.httpClient.get<TabellenKonfiguration>(`${this.baseUrl}/${key}/einstellung`);
  }

  async holeFilterOptionenNutzer(): Promise<FilterOptionen> {
    return this.httpClient.get(`${this.baseUrl}/nutzer/filteroptionen`);
  }

  async holeFilterOptionenNutzerMitUnternehmenId(unternehmenId: string): Promise<FilterOptionen> {
    return this.httpClient.get(`${this.baseUrl}/nutzer/${unternehmenId}/filteroptionen`);
  }

  async holeNutzerMitPaginierungUndFilterung(
    seite: number,
    seitengroesse: number,
    property: string,
    sortierArt: string,
    nutzerFilter: NutzerFilter,
  ): Promise<NutzerFilterResponse> {
    return this.httpClient.post(`${this.baseUrl}/nutzer/filterung`, {
      params: { page: seite, size: seitengroesse, sort: `${property},${sortierArt}` },
      data: nutzerFilter,
    });
  }

  async holeAdminNutzerFuerFremdeOrganisationMitFilterungUndPaginierung(
    seite: number,
    seitengroesse: number,
    property: string,
    sortierArt: string,
    nutzerFilter: NutzerFilter,
    unternehmenId: string,
  ): Promise<NutzerFilterResponse> {
    return this.httpClient.post(`${this.baseUrl}/nutzer/${unternehmenId}/filterung`, {
      params: { page: seite, size: seitengroesse, sort: `${property},${sortierArt}` },
      data: nutzerFilter,
    });
  }
}
